import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdBanner({ placement, className = '' }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchAds();
  }, [placement]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ads/${placement}`);
      setAds(response.data);
    } catch (error) {
      // Silently fail - ads are optional
      console.log('No ads available for placement:', placement);
    }
  };

  const handleClick = async (ad) => {
    try {
      await axios.post(`${API_URL}/api/ads/${ad.id}/click`);
    } catch (err) {
      console.warn('Ad click tracking failed:', err);
    }
    
    if (ad.link_url) {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  if (dismissed || ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentIndex];

  // Determine styling based on placement
  const getPlacementStyles = () => {
    switch (placement) {
      case 'home_top':
        return 'w-full h-40 md:h-48 lg:h-56';
      case 'home_middle':
        return 'w-full h-36 md:h-44 lg:h-48';
      case 'home_bottom':
        return 'w-full h-36 md:h-40 lg:h-44';
      case 'listing_sidebar':
        return 'w-full h-64 md:h-80 lg:h-96';
      case 'listing_bottom':
        return 'w-full h-36 md:h-44 lg:h-48';
      case 'search_top':
        return 'w-full h-32 md:h-40 lg:h-44';
      default:
        return 'w-full h-40';
    }
  };

  return (
    <div className={`relative ${className}`} data-testid={`ad-banner-${placement}`}>
      {currentAd.type === 'video' ? (
        <div className={`relative ${getPlacementStyles()} bg-slate-900 rounded-xl overflow-hidden`}>
          {currentAd.media_url.includes('youtube') || currentAd.media_url.includes('youtu.be') ? (
            <iframe
              src={currentAd.media_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={currentAd.title}
            />
          ) : (
            <video
              src={currentAd.media_url}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          )}
        </div>
      ) : (
        <div
          onClick={() => handleClick(currentAd)}
          className={`relative ${getPlacementStyles()} rounded-xl overflow-hidden cursor-pointer group`}
          style={{ background: 'linear-gradient(to bottom right, #4F46E5, #7C3AED, #4F46E5)' }}
        >
          <img
            src={currentAd.media_url}
            alt={currentAd.title}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          {/* Ad label */}
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 text-white text-[10px] font-medium rounded">
            Ad
          </div>
        </div>
      )}

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
        title="Dismiss ad"
      >
        <X className="w-3 h-3" />
      </button>

      {/* Multiple ads indicator */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
